import { ChangeDetectionStrategy, Component, ElementRef, viewChild, inject } from '@angular/core';
import { FormControl, ValidatorFn, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { ICellEditorParams } from 'ag-grid-community';
import { format, isValid, parse } from 'date-fns';
import { NgClass } from '@angular/common';

export interface IDateStringCellEditorParams extends ICellEditorParams {
    min?: string;
    max?: string;
    step?: number;
    value: string;
    format?: string;
}

@Component({
    template: `
        <input
            [ngClass]="{ 'has-error': dateControl.invalid }"
            #inputElement
            type="date"
            [formControl]="dateControl"
            [step]="params.step"
            [min]="params.min"
            [max]="params.max"
        />
    `,
    styles: [
        `
            :host,
            input {
                width: 100%;
                border: none;
            }
            .has-error {
                color: var(--color-warn);
            }
        `
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ReactiveFormsModule, NgClass]
})
export class DateStringCellEditorComponent implements ICellEditorAngularComp {
    private snackbar = inject(MatSnackBar);

    public readonly defaultFormat = 'yyyy-MM-dd';

    public dateControl = new FormControl<string>(null as any);
    public params!: IDateStringCellEditorParams;

    readonly inputElement = viewChild.required<ElementRef<HTMLInputElement>>('inputElement');

    agInit(params: IDateStringCellEditorParams) {
        this.params = params;
        if (params.value) {
            const asDate = parse(params.value, this.dateFormat, new Date());
            this.dateControl.setValue(format(asDate, this.defaultFormat));
        }
        const validators: ValidatorFn[] = [];
        if (typeof params.min === 'string') {
            const asDate = parse(params.min, this.dateFormat, new Date());
            if (isValid(asDate)) {
                // Min date validator
            }
        }
        if (typeof params.max === 'string') {
            const asDate = parse(params.max, this.dateFormat, new Date());
            if (isValid(asDate)) {
                // Max date validator
            }
        }
        this.dateControl.setValidators(validators);
    }

    afterGuiAttached() {
        this.inputElement().nativeElement.focus();
        this.inputElement().nativeElement.select();
    }

    getValue(): any {
        if (this.dateControl.value) {
            const parsed = parse(this.dateControl.value, this.defaultFormat, new Date());
            if (!!parsed && isValid(parsed)) {
                return format(parsed, this.dateFormat);
            }
        }
        return null;
    }

    focusIn() {
        // NYI
    }

    focusOut() {
        // NYI
    }

    isCancelAfterEnd(): boolean {
        const cancel = this.dateControl.pristine || this.dateControl.invalid;
        if (cancel) {
            const { min, max } = this.params;
            let message = `Invalid date provided.`;
            if (typeof min === 'number' && typeof max === 'number') {
                message += ` Must be between ${min} and ${max}`;
            } else if (typeof min === 'number') {
                message += ` Must be after ${min}`;
            } else if (typeof max === 'number') {
                message += ` Must be before ${max}`;
            }
            this.snackbar.open(message, undefined, { duration: 5000 });
        }
        return cancel;
    }

    isPopup(): boolean {
        return false;
    }

    getPopupPosition(): 'over' | 'under' | undefined {
        return undefined;
    }

    private get dateFormat(): string {
        return this.params.format || this.defaultFormat;
    }
}
